import { signal } from '@preact/signals';
import { sum, throttle } from 'es-toolkit';
import * as z from 'zod/mini';
import { create } from 'zustand';
import { useShallow } from 'zustand/shallow';
import { EpisodeCompoundId, PodcastId } from '../models/podcasts';
import { APIError, requestApi } from '../utils/api';
import debugLog from '../utils/debug';
import { reportSentryError, reportSentryWarning } from '../utils/error-handling';
import {
  exposeOnWindow,
  isEpisodeIdentifier,
  isObjectWithProperties,
  isPodcastIdentifier,
  isWithin,
} from '../utils/helpers';
import {
  getTypedInitialValue,
  setLazyValue,
  wasRecentlyHidden,
} from '../utils/localstorage';
import { isNativeAppWithStateV1, platform } from '../utils/platform';
import { trackSkipAdInPlausible } from '../utils/plausible-events';
import { currentEpisode, getState } from './audio-signals';
import { idToEpisode } from './podcasts';
import { trackRecentAdSkip, wasAdRecentlySkipped } from './session';
import { getSignedInState, getUser } from './user';

const log = (...args: any[]) => {
  debugLog({ logName: 'usage', logColor: 'orange' }, ...args);
};

//
// Track skipped ads and roughly how long podcasts are listened to
//

type UsageStore = {
  // Track ad skips & minutes listened of each podcast
  podcasts: Record<
    string,
    {
      adSkips: number;
      minutesListened: number;
    }
  >;
  // Store ad skips remaining (provided by API)
  adSkipsLeft: {
    free: number;
    expiring: number;
    forever: number;
    renewalTimestamp: number | undefined;
  } | null;
  // Users current Stripe subscription status
  subscriptionStatus: string | undefined;
  subscriptionStore: string | undefined;
  accountManagementUrl: 'STRIPE_CUSTOMER_PORTAL' | `${string}://${string}` | undefined;
};
export const totalAdSkipsLeft = signal<number>(NaN);

const usageDataPodcastsSchema = z.record(
  z.string(),
  z.object({
    adSkips: z.int().check(z.gte(0)),
    minutesListened: z.int().check(z.gte(0)),
  })
);

const usageDataAslSchema = z.nullable(
  z.object({
    free: z.int().check(z.gte(0)),
    expiring: z.int().check(z.gte(0)),
    forever: z.int().check(z.gte(0)),
    renewalTimestamp: z.union([z.number(), z.undefined()]),
  })
);

type UsageStoreActions = {
  _trackAdSkip: (podcastId: PodcastId, duration: number, credits: number) => void;
  addMinutesListened: (podcastId: PodcastId, minutes: number) => void;
  bumpMinutesListened: (podcastId: PodcastId) => void;
  clearUsageAndSubscriptionData: () => void;
};

const useUsageStore = create<UsageStore & { actions: UsageStoreActions }>()(
  (set, get) => ({
    podcasts: getTypedInitialValue(
      'UsageStore__podcasts',
      (val) => usageDataPodcastsSchema.parse(val),
      {}
    ),
    adSkipsLeft: getTypedInitialValue(
      'UsageStore__asl',
      (val) => usageDataAslSchema.parse(val),
      null
    ),
    subscriptionStatus: undefined,
    subscriptionStore: undefined,
    accountManagementUrl: undefined,
    actions: {
      _trackAdSkip: (podcastId, duration, credits) => {
        const { podcasts, subscriptionStatus, subscriptionStore } = get();
        const podcast = podcasts[podcastId] || { adSkips: 0, minutesListened: 0 };
        let { adSkips, minutesListened } = podcast;
        adSkips += credits;
        log(`Consumed ${credits} ad skip credits`);
        set({
          podcasts: { ...podcasts, [podcastId]: { adSkips, minutesListened } },
        });
        trackSkipAdInPlausible({
          subscriptionStore: subscriptionStore || 'no_store',
          subscriptionStatus: subscriptionStatus || 'no_subscription',
          duration,
          credits,
        });
      },
      addMinutesListened: (podcastId, minutes) => {
        if (!(minutes > 0)) return;
        const { podcasts } = get();
        const podcast = podcasts[podcastId] || { adSkips: 0, minutesListened: 0 };
        let { adSkips, minutesListened } = podcast;
        minutesListened += minutes;
        log(
          `Added ${minutes} minutes listened to ${podcastId} (${minutesListened - minutes} -> ${minutesListened})`
        );
        set({
          podcasts: { ...podcasts, [podcastId]: { adSkips, minutesListened } },
        });
      },
      bumpMinutesListened: (podcastId) => {
        const { podcasts } = get();
        const podcast = podcasts[podcastId] || { adSkips: 0, minutesListened: 0 };
        let { adSkips, minutesListened } = podcast;
        set({
          podcasts: {
            ...podcasts,
            [podcastId]: { adSkips, minutesListened: minutesListened + 1 },
          },
        });
      },
      clearUsageAndSubscriptionData: () =>
        set({
          podcasts: {},
          adSkipsLeft: null,
          // Reset user-related data as well
          subscriptionStatus: undefined,
          subscriptionStore: undefined,
          accountManagementUrl: undefined,
        }),
    },
  })
);

const initialAdSkipsLeft = _getAdSkipsLeft(useUsageStore.getState());
totalAdSkipsLeft.value = initialAdSkipsLeft.isLoading ? NaN : initialAdSkipsLeft.total;

//
// Expose actions
//

export const usageActions = useUsageStore.getState().actions;

export function trackAdSkipUnlessRecent(
  episodeId: EpisodeCompoundId,
  startTime: number,
  endTime: number
) {
  if (!isEpisodeIdentifier(episodeId))
    return reportSentryError(new Error('Invalid episodeId during ad-skip tracking'), {
      episodeId,
    });

  if (!(startTime < endTime))
    return reportSentryError(
      new Error('Invalid start/end time during ad-skip tracking'),
      {
        episodeId,
        startTime,
        endTime,
      }
    );

  const wasRecentlySkipped = wasAdRecentlySkipped(episodeId, startTime);

  // Add this as the most recent ad skip (after checking if it was recently skipped)
  trackRecentAdSkip(episodeId, startTime);

  if (wasRecentlySkipped) return log('Ad recently skipped. Not consuming credit...');

  // Track ad skip & consume credits
  const adDuration = endTime - startTime;
  usageActions._trackAdSkip(
    episodeId.split('|')[0] as PodcastId,
    adDuration,
    determineCredits(episodeId, startTime, adDuration)
  );
}

// Figure out ad-skip credit amount (with fallback for failure cases)
export function determineCredits(
  episodeId: EpisodeCompoundId,
  startTime: number,
  adDuration: number
): number {
  const episode = idToEpisode(episodeId);
  if (!episode || !episode.ads || !episode.duration) {
    reportSentryError(new Error('Failed to get episode for ad-skip calculation'), {
      episodeId,
      startTime,
      episodeAds: !episode ? 'no episode' : episode.ads,
      episodeDuration: !episode ? 'no episode' : episode.duration,
    });
    return adDurationToAdSkipCredits(adDuration);
  }

  const adsWithCredits = getAdsWithCredits(episode.ads, episode.duration);
  const ad = adsWithCredits.find((ad) => isWithin(ad.startTime, startTime, 1));
  if (!ad) {
    reportSentryError(new Error('Failed to match ad for ad-skip calculation'), {
      episodeId,
      startTime,
      ads: adsWithCredits.map((ad) => ad.startTime),
    });
    return adDurationToAdSkipCredits(adDuration);
  }

  return ad.credits;
}

export const useAdSkipsLeft = () => useUsageStore(useShallow(_getAdSkipsLeft));
export const getAdSkipsLeft = () => _getAdSkipsLeft(useUsageStore.getState());

function _getAdSkipsLeft(s: UsageStore) {
  if (!s.adSkipsLeft) {
    return {
      free: 0,
      expiring: 0,
      forever: 0,
      total: 0,
      isLoading: true,
      description: 'Loading ad-skips...',
    };
  }

  let pendingSync = sum(Object.values(s.podcasts).map((o) => o.adSkips));
  const [free, expiring, forever] = subtractFromValues(
    [s.adSkipsLeft.free, s.adSkipsLeft.expiring, s.adSkipsLeft.forever],
    pendingSync
  );
  const total = free + expiring + forever;
  // console.log({ free, expiring, forever, total });

  const adSkipQuantityText =
    [
      free ? `${free} free` : '',
      expiring ? `${expiring} monthly` : '',
      forever ? `${forever} non-expiring` : '',
    ]
      .filter(Boolean)
      .join(' & ') || 'No';

  const renewsInDays = s.adSkipsLeft.renewalTimestamp
    ? (s.adSkipsLeft.renewalTimestamp - Date.now()) / (1000 * 60 * 60 * 24)
    : NaN;

  const renewalText =
    renewsInDays > 1
      ? `Monthly ad-skips will renew in ${Math.round(renewsInDays)} days.`
      : renewsInDays < 1
        ? `Monthly ad-skips will renew in ${Math.ceil(renewsInDays * 24)} hours.`
        : '';

  const adSkipText = total === 1 ? 'ad-skip' : 'ad-skips';

  return {
    free,
    expiring,
    forever,
    total,
    isLoading: false,
    description: `${adSkipQuantityText} ${adSkipText} left. ${renewalText}`,
  };
}

export const useSubscriptionStatus = () => useUsageStore((s) => s.subscriptionStatus);
export const useSubscriptionStore = () => useUsageStore((s) => s.subscriptionStore);
export const useAccountUrl = () => useUsageStore((s) => s.accountManagementUrl);

//
// Save updates in LocalStorage
//

useUsageStore.subscribe(({ podcasts, adSkipsLeft }) => {
  // Update signal
  const { total, isLoading } = getAdSkipsLeft();
  totalAdSkipsLeft.value = isLoading ? NaN : total;

  setLazyValue('UsageStore__podcasts', podcasts);
  setLazyValue('UsageStore__asl', adSkipsLeft);
  syncUsageDataLazily('usage-store');
});

// Report any invalid usage entries
useUsageStore.subscribe(({ podcasts }) => {
  const invalidEntries = getInvalidUsageEntries(podcasts);
  if (!invalidEntries.length) {
    log('Usage changed', podcasts); // Debug
    return;
  }

  reportSentryWarning(new Error('Invalid usage entries in store'), {
    invalidEntries,
    podcasts,
  });
});

//
// Poll for currently playing podcast once a minute
//

let lastPodcastId: PodcastId | null = null;
let debugLastPoll = {};
let isPollingManagedByNativeApp = false;
export function pollForPodcastEveryMinute(isTriggeredByNativeApp = false) {
  // If native app queues this polling, then no longer queue it with setTimeout
  if (isTriggeredByNativeApp) isPollingManagedByNativeApp = true;
  if (isPollingManagedByNativeApp && !isTriggeredByNativeApp) return;

  const state = getState();
  const { podcastId } = currentEpisode.peek() || {};
  Object.assign(debugLastPoll, { state, podcastId, lastPodcastId, time: Date.now() });
  log('polling', state, podcastId);

  const isOnSamePodcast = lastPodcastId === podcastId;
  lastPodcastId = podcastId || null;

  if (isOnSamePodcast && podcastId && ['playing', 'seeking'].includes(state)) {
    // We've been listening to this same podcast for a minute, bump it
    usageActions.bumpMinutesListened(podcastId);
  }

  if (!isPollingManagedByNativeApp) setTimeout(pollForPodcastEveryMinute, 1000 * 60);
}

// Start polling soon after loading this page
if (typeof window !== 'undefined' && !isNativeAppWithStateV1) {
  setTimeout(pollForPodcastEveryMinute, 1000 * 15);
}

//
// Sync to API (Supabase)
//

let isSyncing = false;
let lastSyncAt = 0;
const fiveMinutes = 5 * 60 * 1000;

// Lazily sync usage data
export const syncUsageDataLazily = throttle(syncUsageDataToApi, fiveMinutes);

// Sync usage data immediately
export const syncUsageDataNow = (source: string) => {
  syncUsageDataLazily(source);
  syncUsageDataLazily.flush();
};

// Sync when returning to the app (if it's been over a minute), since backgrounding
// is a poor time to wait on an API response that we need before clearing local usage state.
if (typeof window !== 'undefined') {
  addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() > lastSyncAt + 60 * 1000) {
      // Wait a moment for native app to sync latest usage data
      setTimeout(() => syncUsageDataNow('visibilitychange'), 100);
    }
  });
}

export async function syncUsageDataToApi(source: string) {
  if (isSyncing) {
    log('Ignoring duplicate sync request', { source });
    return;
  }

  const user = getUser();
  if (!user) {
    log("Not syncing b/c there's no user");
    return;
  }

  if (getSignedInState() === 'loading') return;

  interface ResponseData {
    data: {
      updatedPodcastIds: string[];
      freeAdSkipsLeft: number;
      expiringAdSkipsLeft: number;
      foreverAdSkipsLeft: number;
      renewalTimestamp: number | undefined;
      subscriptionStatus: string | undefined;
      subscriptionStore: string | undefined;
      accountManagementUrl:
        | 'STRIPE_CUSTOMER_PORTAL'
        | `${string}://${string}`
        | undefined;
    };
    nonFatalErrors?: Record<string, any>;
  }

  // Mark syncing before consuming native usage. That consumption updates this store,
  // and the store subscriber may otherwise re-enter syncUsageDataToApi before the
  // native app has acknowledged/cleared the just-consumed seconds.
  lastSyncAt = Date.now();
  isSyncing = true;
  window.dispatchEvent(new Event('USAGE_SYNC_ABOUT_TO_START'));

  const dataToSync = {
    userId: user.id,
    podcasts: useUsageStore.getState().podcasts,
    env: location.origin.endsWith('www.adblockpodcast.com') ? 'prod' : 'test',
    platform: platform.toLowerCase(),
    source,
  };

  log('Syncing usage data to API', source, dataToSync);
  let results: ResponseData['data'];
  try {
    const response = (
      await requestApi(
        'https://didgjhjnevdixtjltmho.functions.supabase.co/sync-usage-and-get-subscriber',
        dataToSync,
        { Authorization: `Bearer ${user.accessToken}` }
      )
    ).json as ResponseData;
    if (!response.data) throw new APIError('Invalid response');
    if (response.nonFatalErrors)
      reportSentryError(new Error('Usage reporting failures'), response.nonFatalErrors);
    results = response.data;
    isSyncing = false;
  } catch (error) {
    // Ensure isSyncing is always set to false
    isSyncing = false;
    if (!wasRecentlyHidden())
      reportSentryError(error as Error, {
        context: 'Failed to report usage data',
        source,
      });
    return;
  }

  // If user data has changed since sync started, don't do anything with result
  const currentUser = getUser();
  if (!currentUser || currentUser.id !== user.id) {
    log("Ignoring results of reporting prior user's usage");
    return;
  }

  const podcastsCloned: UsageStore['podcasts'] = JSON.parse(
    JSON.stringify(useUsageStore.getState().podcasts)
  ) as any;

  // Keep debugging weird state!
  log('After syncing usage data', {
    dataToSyncPodcasts: dataToSync.podcasts,
    podcastsCloned,
    results,
  });

  results.updatedPodcastIds.forEach((podcastId) => {
    const { adSkips: reportedAdSkips, minutesListened: reportedMinutesListened } =
      dataToSync.podcasts[podcastId];
    if (podcastsCloned[podcastId]) {
      const before = { ...podcastsCloned[podcastId] };

      podcastsCloned[podcastId].adSkips -= reportedAdSkips;
      podcastsCloned[podcastId].minutesListened -= reportedMinutesListened;

      // Report case where minutes listened is less than zero sometimes
      if (
        podcastsCloned[podcastId].adSkips < 0 ||
        podcastsCloned[podcastId].minutesListened < 0
      ) {
        reportSentryWarning(new Error('Negative usage values after update'), {
          before,
          after: podcastsCloned[podcastId],
          synced: dataToSync.podcasts[podcastId],
        });
      }
    }
  });
  // Remove entries without any usage that has yet to be reported
  Object.keys(podcastsCloned).forEach((podcastId) => {
    const { adSkips, minutesListened } = podcastsCloned[podcastId];
    if (!adSkips && !minutesListened) {
      // console.log('Clearing reported usage', podcastId, podcastsCloned[podcastId]);
      delete podcastsCloned[podcastId];
    }
  });
  useUsageStore.setState({
    podcasts: podcastsCloned,
    adSkipsLeft: {
      free: results.freeAdSkipsLeft,
      expiring: results.expiringAdSkipsLeft,
      forever: results.foreverAdSkipsLeft,
      renewalTimestamp: results.renewalTimestamp,
    },
    subscriptionStatus: results.subscriptionStatus,
    subscriptionStore: results.subscriptionStore,
    accountManagementUrl: results.accountManagementUrl,
  });
}

//

//
// Utils
//

function subtractFromValues(values: number[], amountToSubtract: number) {
  return values.map((val) => {
    if (amountToSubtract <= 0) return val;

    if (val >= amountToSubtract) {
      val = val - amountToSubtract;
      amountToSubtract = 0;
      return val;
    }

    amountToSubtract -= val;
    return 0;
  });
}

type Ad = { startTime: number; endTime: number };
const maxCreditsPerHour = 10;

export function getAdsWithCredits(
  ads: Ad[],
  episodeDuration: number
): (Ad & { credits: number })[] {
  const adsWithCredits = ads.map((ad) => ({
    ...ad,
    credits: adDurationToAdSkipCredits(ad.endTime - ad.startTime),
  }));
  const initialTotal = sum(adsWithCredits.map((ad) => ad.credits));
  const maxCredits = Math.ceil((maxCreditsPerHour * episodeDuration) / 3600);

  // Decrement credits in a round-robin fashion
  if (initialTotal > maxCredits) {
    // const adWithCreditsBefore = cloneDeep(adsWithCredits);
    let excessCredits = initialTotal - maxCredits;
    let adIndex = 0;
    while (excessCredits > 0) {
      if (adsWithCredits[adIndex].credits > 0) {
        adsWithCredits[adIndex].credits -= 1;
        excessCredits -= 1;
      }
      adIndex = (adIndex + 1) % adsWithCredits.length;
    }
    // console.log({
    //   adWithCreditsBefore,
    //   adsWithCredits,
    //   initialTotal,
    //   maxCredits,
    //   episodeDuration,
    // });
    // if (sum(adsWithCredits.map((ad) => ad.credits)) !== maxCredits) {
    //   throw new Error('Failed to round-robin credits');
    // }
  }

  return adsWithCredits;
}

// Convert ad duration to number of ad skip credits required (before deducting round-robin style if over max credits / episode duration)
export function adDurationToAdSkipCredits(duration: number) {
  let adSkips = 0;
  if (duration >= 10) adSkips += 1;
  if (duration >= 40) adSkips += 1;
  if (duration > 90) adSkips += 1;
  return adSkips;
}

exposeOnWindow({ useUsageStore, debugLastPoll });

function isValidUsageEntry(value: unknown): value is {
  adSkips: number;
  minutesListened: number;
} {
  if (!isObjectWithProperties(value, ['adSkips', 'minutesListened'])) return false;
  const usage = value as { adSkips: unknown; minutesListened: unknown };
  return (
    Number.isInteger(usage.adSkips) &&
    Number.isInteger(usage.minutesListened) &&
    (usage.adSkips as number) >= 0 &&
    (usage.minutesListened as number) >= 0
  );
}

function getInvalidUsageEntries(
  podcasts: Record<string, { adSkips: number; minutesListened: number }>
) {
  return Object.entries(podcasts).flatMap(([podcastId, usage]) =>
    isPodcastIdentifier(podcastId) && isValidUsageEntry(usage)
      ? []
      : [[podcastId, usage] as const]
  );
}
